import { usePermission } from "src/helpers/Auth";
import { useGetDataset } from "../services/DatasetServices";
import { Dataset } from "../types";

/**
 * Whether the current user may reprocess a record in `datasetId`. Mirrors the
 * backend's two-axis check on the reprocess routes: the role must hold the
 * `dataset_data:write` capability AND the user must hold WRITE on this
 * specific dataset. Reprocessing deletes and rebuilds the document's chunks
 * and embeddings, so it is write-gated per-object — visibility=public grants
 * read only, and adminship confers no content write.
 *
 * Sibling of {@link useCanDeleteDatasetRecords}; see its note on why this
 * lives in the viewer layer rather than DatasetServices.
 *
 * Strict: returns false while the dataset (and its my_access) is loading, so
 * the affordance never appears before its authority is known.
 */
export const useCanReprocessDatasetRecords = (
  datasetId: number | undefined,
): boolean => {
  const { canIt: hasWriteCapability } = usePermission("dataset_data:write");
  const { data: dataset } = useGetDataset(datasetId) as {
    data: Dataset | undefined;
  };
  return Boolean(hasWriteCapability) && dataset?.my_access?.can_write === true;
};
